import { ZoneType, PickHeight } from '@slot-matrix/shared-types';
import { parseTemperatureReq } from './temperatureConstraint';
const SEGMENT_ORDER = ['AX', 'AY', 'AZ', 'BX', 'BY', 'BZ', 'CX', 'CY', 'CZ'];
/**
 * Build O(1) lookup maps joining locations → zones and skus → xyz results.
 * Pure: performs no I/O. Used to avoid repeated `.find` scans inside the
 * hot scoring loop.
 */
export function buildLookups(zones, locations, xyzResults) {
    const zoneById = new Map(zones.map((z) => [z.id, z]));
    const zoneByLocationId = new Map();
    for (const loc of locations) {
        const z = zoneById.get(loc.zoneId);
        if (z)
            zoneByLocationId.set(loc.id, z);
    }
    const xyzBySkuId = new Map(xyzResults.map((r) => [r.skuId, r]));
    return { zoneByLocationId, xyzBySkuId };
}
const EMPTY_LOOKUPS = {
    zoneByLocationId: new Map(),
    xyzBySkuId: new Map(),
};
/**
 * Score a location's suitability for a given SKU.
 * Higher scores are better. Negative scores indicate hard constraint violations.
 */
export function scoreLocation(sku, location, zones, rules, _config, segment, lookups = EMPTY_LOOKUPS) {
    let score = 0;
    const zone = lookups.zoneByLocationId.get(location.id) ?? zones.find((z) => z.id === location.zoneId);
    if (!zone)
        return -1000;
    // Find matching rule
    const matchingRule = rules.find((r) => r.segment === segment);
    // Rule matching (segment → zone)
    if (matchingRule) {
        if (zone.type === matchingRule.targetZoneType) {
            score += 40 * (10 - matchingRule.priority) / 9;
        }
        if (location.pickHeight === matchingRule.targetPickHeight) {
            score += 15;
        }
    }
    // Travel distance (inverse — closer is better)
    score += 20 / Math.max(1, zone.distanceScore);
    // Dimension fit
    if (fitsDimension(sku.dimensions, location.dimensions)) {
        score += 10;
    }
    else {
        return -1000; // HARD CONSTRAINT
    }
    // Weight constraint
    if (sku.weight <= location.weightCapacity) {
        score += 5;
    }
    else {
        return -1000; // HARD CONSTRAINT
    }
    // Hazmat constraint
    if (sku.hazmatClass && zone.type !== ZoneType.Hazmat) {
        return -1000; // HARD CONSTRAINT
    }
    // Temperature / cold-chain constraint (TODO-04)
    const tempReq = parseTemperatureReq(sku.temperatureReq);
    if (tempReq) {
        if (!zone.temperatureRange ||
            tempReq.max < zone.temperatureRange.min ||
            tempReq.min > zone.temperatureRange.max) {
            return -1000; // HARD CONSTRAINT — incompatible temperature
        }
    }
    // Fragile-item placement guard (TODO-06): never put fragile SKUs overhead
    if (sku.fragile && location.pickHeight === PickHeight.Overhead) {
        return -1000; // HARD CONSTRAINT
    }
    // Ergonomic bonus for golden zone
    if ((location.pickHeight === PickHeight.Waist || location.pickHeight === PickHeight.Shoulder) &&
        segment.startsWith('A')) {
        const xyz = lookups.xyzBySkuId.get(sku.id);
        if (xyz?.isIntermittent) {
            // Intermittent A-items lose the golden-zone premium (TODO-07)
            score -= 15;
        }
        else {
            score += 10;
        }
    }
    return score;
}
function fitsDimension(skuDims, locDims) {
    return skuDims.length <= locDims.length &&
        skuDims.width <= locDims.width &&
        skuDims.height <= locDims.height;
}
/** Whether a location is rejected specifically due to temperature mismatch. */
function isTemperatureViolation(sku, zone) {
    const tempReq = parseTemperatureReq(sku.temperatureReq);
    if (!tempReq)
        return false;
    if (!zone || !zone.temperatureRange)
        return true;
    return tempReq.max < zone.temperatureRange.min || tempReq.min > zone.temperatureRange.max;
}
/**
 * Optimize slot assignments for all SKUs.
 * Deterministic: same input always produces the same output.
 */
export function optimizeSlotting(skus, locations, zones, rules, config, segments) {
    if (skus.length === 0 || locations.length === 0)
        return [];
    const lookups = buildLookups(zones, locations, []);
    // Sort SKUs by segment priority, then by metric value (descending)
    const sortedSkus = [...skus].sort((a, b) => {
        const segA = segments.get(a.id) ?? 'CZ';
        const segB = segments.get(b.id) ?? 'CZ';
        const idxA = SEGMENT_ORDER.indexOf(segA);
        const idxB = SEGMENT_ORDER.indexOf(segB);
        if (idxA !== idxB)
            return idxA - idxB;
        // Within same segment, sort by weight (lighter first for better packing)
        return a.weight - b.weight;
    });
    // Sort locations by desirability for high-priority items
    const sortedLocations = [...locations].sort((a, b) => {
        const zoneA = zones.find((z) => z.id === a.zoneId);
        const zoneB = zones.find((z) => z.id === b.zoneId);
        // Golden zone first, then by distance score (lower is better), then by pick height preference
        const typeOrder = {
            [ZoneType.GoldenZone]: 0,
            [ZoneType.ForwardPick]: 1,
            [ZoneType.Reserve]: 2,
            [ZoneType.Bulk]: 3,
            [ZoneType.Oversize]: 4,
            [ZoneType.ColdStorage]: 5,
            [ZoneType.Hazmat]: 6,
        };
        const typeA = zoneA ? typeOrder[zoneA.type] ?? 9 : 9;
        const typeB = zoneB ? typeOrder[zoneB.type] ?? 9 : 9;
        if (typeA !== typeB)
            return typeA - typeB;
        const distA = zoneA?.distanceScore ?? 99;
        const distB = zoneB?.distanceScore ?? 99;
        if (distA !== distB)
            return distA - distB;
        // Prefer waist/shoulder height
        const heightOrder = {
            [PickHeight.Shoulder]: 0,
            [PickHeight.Waist]: 1,
            [PickHeight.Floor]: 2,
            [PickHeight.Overhead]: 3,
        };
        return (heightOrder[a.pickHeight] ?? 9) - (heightOrder[b.pickHeight] ?? 9);
    });
    // Track available locations (not frozen, not already occupied)
    const available = new Set(sortedLocations.filter((l) => !l.isFrozen).map((l) => l.id));
    const locationMap = new Map(sortedLocations.map((l) => [l.id, l]));
    const assignments = [];
    for (const sku of sortedSkus) {
        const segment = segments.get(sku.id) ?? 'CZ';
        let bestLocationId = null;
        let bestScore = -Infinity;
        const constraintViolations = [];
        const skuTempReq = parseTemperatureReq(sku.temperatureReq);
        let tempRejectedCount = 0;
        let scannedCount = 0;
        for (const locId of available) {
            const loc = locationMap.get(locId);
            scannedCount++;
            const score = scoreLocation(sku, loc, zones, rules, config, segment, lookups);
            if (score < 0 && skuTempReq) {
                const zone = lookups.zoneByLocationId.get(loc.id);
                if (isTemperatureViolation(sku, zone))
                    tempRejectedCount++;
            }
            if (score > bestScore) {
                bestScore = score;
                bestLocationId = locId;
            }
        }
        // Find current location
        const currentLoc = locations.find((l) => l.currentSkuId === sku.id);
        const currentLocationId = currentLoc?.id ?? null;
        if (bestLocationId !== null && bestScore >= 0) {
            available.delete(bestLocationId);
            const rationale = [];
            const matchingRule = rules.find((r) => r.segment === segment);
            if (matchingRule) {
                const zone = zones.find((z) => z.id === locationMap.get(bestLocationId).zoneId);
                if (zone && zone.type === matchingRule.targetZoneType) {
                    rationale.push(`${segment} → ${zone.type}`);
                }
            }
            if (segment.startsWith('A') && ['waist', 'shoulder'].includes(locationMap.get(bestLocationId).pickHeight)) {
                rationale.push('Ergonomic height for high-velocity');
            }
            if (!currentLocationId) {
                rationale.push('New SKU placement');
            }
            else if (currentLocationId !== bestLocationId) {
                rationale.push('Better zone/height match');
            }
            assignments.push({
                skuId: sku.id,
                recommendedLocationId: bestLocationId,
                currentLocationId,
                segment,
                movePriority: assignments.length + 1,
                impactScore: Math.min(100, Math.max(0, bestScore)),
                travelReduction: 0,
                rationale,
                constraintViolations,
            });
        }
        else {
            // No valid location found — surface temperature-specific cause when applicable
            if (skuTempReq && scannedCount > 0 && tempRejectedCount === scannedCount) {
                constraintViolations.push(`No location satisfies temperature requirement (${sku.temperatureReq})`);
            }
            else {
                constraintViolations.push('No location passes all constraints');
            }
            assignments.push({
                skuId: sku.id,
                recommendedLocationId: currentLocationId ?? '',
                currentLocationId,
                segment,
                movePriority: assignments.length + 1,
                impactScore: 0,
                travelReduction: 0,
                rationale: ['No valid location found'],
                constraintViolations,
            });
        }
    }
    // Calculate travel reduction and impact scores
    for (const assignment of assignments) {
        if (assignment.currentLocationId && assignment.currentLocationId !== assignment.recommendedLocationId) {
            const currentZone = zones.find((z) => z.id === locations.find((l) => l.id === assignment.currentLocationId)?.zoneId);
            const newZone = zones.find((z) => z.id === locations.find((l) => l.id === assignment.recommendedLocationId)?.zoneId);
            if (currentZone && newZone) {
                assignment.travelReduction = (currentZone.distanceScore - newZone.distanceScore) * 10;
            }
        }
    }
    // Sort by impact score descending to assign move priorities
    assignments.sort((a, b) => b.impactScore - a.impactScore);
    assignments.forEach((a, i) => {
        a.movePriority = i + 1;
    });
    return assignments;
}
//# sourceMappingURL=slottingOptimizer.js.map